import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../lib/api'
import type { Chat, Topic } from '../lib/apiModels'
import {
  doneHeading,
  formatClock,
  hoursIn,
  shouldShowPanel,
  TURN_SUMMARY_POLL_MS,
  turnSummaryPath,
  type TurnSummaryResponse,
} from '../lib/doneSoFar'

/**
 * Collapsible 'Done so far' drawer for a long-running turn.
 *
 * Read-only: polls the turn-summary endpoint, which builds an extractive
 * summary from the turn's existing progress stream at 15 min, 1 h and then
 * hourly. Opening or closing it never contacts the lane or the agent.
 */
export default function DoneSoFarPanel({
  chatId,
  chat,
  topic,
}: {
  chatId: string
  chat: Chat
  topic: Topic | null
}) {
  const [open, setOpen] = useState(false)
  const topicId = topic?.id ?? null
  const conversation = topic ?? (chat.type === 'user-driven' ? chat : null)
  const activeSource = conversation?.active_source_message_id ?? null
  const query = useQuery({
    queryKey: ['turn_summary', chatId, topicId, activeSource],
    queryFn: () => api<TurnSummaryResponse>(turnSummaryPath(chatId, topicId)),
    enabled: Boolean(activeSource && !chat.archived),
    refetchInterval: TURN_SUMMARY_POLL_MS,
    staleTime: 30_000,
  })
  const data = query.data
  if (!activeSource || !shouldShowPanel(data) || !data?.summary) return null
  const summary = data.summary
  const nowSeconds = Date.now() / 1000

  return (
    <div className="px-3 pt-2" data-testid="done-so-far">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-11 flex items-center justify-between gap-2 px-4 py-2 rounded-xl text-sm border border-stone-300 dark:border-neutral-700 bg-stone-50 dark:bg-neutral-900 text-stone-800 dark:text-neutral-200"
      >
        <span className="font-medium">Done so far</span>
        <span className="text-xs text-stone-500 dark:text-neutral-400">
          {data.started_at ? `${hoursIn(data.started_at, nowSeconds)} in · ` : ''}
          last updated {formatClock(data.updated_at!)} {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="mt-1 px-4 py-3 rounded-xl text-sm border border-stone-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-stone-800 dark:text-neutral-200 max-h-72 overflow-y-auto">
          {summary.done.length > 0 && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-neutral-400">
                {doneHeading(summary)}
              </div>
              <ul className="mt-1 mb-2 list-disc pl-5 space-y-1">
                {summary.done.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </>
          )}
          {summary.now && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-neutral-400">
                Working on now
              </div>
              <p className="mt-1">{summary.now}</p>
            </>
          )}
          <p className="mt-2 text-xs text-stone-500 dark:text-neutral-400">
            {summary.steps} progress steps · next update {data.next_update_at ? formatClock(data.next_update_at) : '—'}
          </p>
        </div>
      )}
    </div>
  )
}
